import React from 'react';
import { Helmet } from 'react-helmet-async';
import { designTokens } from '../utils/designTokens';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-spooky-bg-darker">
      <Helmet>
        <title>Privacy Policy - Boop</title>
        <meta name="description" content="Privacy Policy for Boop - How we collect, use, and protect your personal information." />
      </Helmet>

      <div className={`max-w-4xl mx-auto px-${designTokens.spacing.pagePadding} py-12`}>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Privacy Policy</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">Last Updated: October 31, 2025</p>

        <div className="prose prose-lg dark:prose-invert max-w-none">

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Introduction</h2>
            <p className="text-gray-700 dark:text-gray-300">
              Boop ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you use our platform. Please read this
              policy carefully.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">Account Information (Required)</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Email address</li>
              <li>Username</li>
              <li>Display name</li>
              <li>Password (hashed and encrypted via Firebase Authentication)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">Profile Information (Optional)</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Profile photo/avatar</li>
              <li>Bio and description</li>
              <li>Social media links</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">Content You Create</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Prompts, ratings, and reviews</li>
              <li>Comments and discussions</li>
              <li>Collections and bookmarks</li>
              <li>Forum posts and community contributions</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">Usage Data</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Pages viewed and features used</li>
              <li>Time spent on platform</li>
              <li>Search queries</li>
              <li>Click patterns and navigation paths</li>
              <li>Device information (browser, OS, screen size)</li>
              <li>IP address and approximate location (city/country)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">Payment Information</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Processed by Stripe and PayPal (we do not store credit card numbers)</li>
              <li>Transaction history and purchase records</li>
              <li>Billing address (for tax compliance)</li>
              <li>Payout details for sellers (PayPal email, bank account via Stripe Connect)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">Cookies and Tracking</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Session cookies for authentication</li>
              <li>Preference cookies (dark mode, language)</li>
              <li>Analytics cookies (Google Analytics, anonymized)</li>
              <li>Performance monitoring (error tracking)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. How We Use Your Information</h2>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">To Provide and Improve Services</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Create and manage your account</li>
                <li>• Display your content and profile</li>
                <li>• Process payments and payouts</li>
                <li>• Send transactional notifications (ratings, comments, follows)</li>
                <li>• Improve platform features and user experience</li>
                <li>• Analyze usage patterns and optimize performance</li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">To Ensure Security and Compliance</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Prevent fraud, spam, and abuse</li>
                <li>• Enforce Terms of Service and Community Guidelines</li>
                <li>• Comply with legal obligations and requests</li>
                <li>• Protect user safety and platform integrity</li>
              </ul>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">To Communicate With You</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Send account-related notifications</li>
                <li>• Respond to support requests</li>
                <li>• Announce new features and updates (optional)</li>
                <li>• Send marketing communications (opt-in only)</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. Information Sharing and Disclosure</h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">What's Public</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Username, display name, and avatar</li>
              <li>Prompts, ratings, reviews, and comments you create</li>
              <li>Your public profile page and bio</li>
              <li>Badges, awards, and leaderboard rankings</li>
              <li>Activity feed (follows, likes, forks)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">What's Private</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Email address (not displayed publicly)</li>
              <li>Payment information and transaction details</li>
              <li>IP address and precise location</li>
              <li>Private messages and support tickets</li>
              <li>Analytics and usage metrics (aggregated only)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">Third-Party Service Providers</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-3">We share data with trusted partners who help us operate:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li><strong>Firebase (Google):</strong> Authentication and user management</li>
              <li><strong>Supabase:</strong> Database hosting and storage</li>
              <li><strong>Stripe:</strong> Payment processing for Boop Coin purchases</li>
              <li><strong>PayPal:</strong> Alternative payment processing and payouts</li>
              <li><strong>Vercel/Railway:</strong> Application hosting and CDN</li>
              <li><strong>Google Analytics:</strong> Anonymized usage analytics</li>
              <li><strong>OpenAI:</strong> AI-powered features (prompts are not stored by OpenAI)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">Legal Requirements</h3>
            <p className="text-gray-700 dark:text-gray-300">
              We may disclose information if required by law, subpoena, court order, or government request, or to
              protect the rights, property, or safety of Boop, our users, or others.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">What We DON'T Do</h3>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>✗ We NEVER sell your personal data to third parties</li>
                <li>✗ We NEVER share your email for marketing without consent</li>
                <li>✗ We NEVER use your prompts to train our own AI models</li>
                <li>✗ We NEVER track you across other websites</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Your Privacy Rights</h2>

            <div className="space-y-4">
              <div className="border-l-4 border-purple-600 dark:border-purple-400 pl-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Access Your Data</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  View and download all your data through Settings → Export Data
                </p>
              </div>

              <div className="border-l-4 border-purple-600 dark:border-purple-400 pl-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Correct Information</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Update your profile, email, and account settings at any time
                </p>
              </div>

              <div className="border-l-4 border-purple-600 dark:border-purple-400 pl-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete Your Account</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Permanently delete your account via Settings → Delete Account. This removes personal information
                  but may anonymize your public content to preserve community context.
                </p>
              </div>

              <div className="border-l-4 border-purple-600 dark:border-purple-400 pl-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Opt Out of Marketing</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Unsubscribe from promotional emails via the link in any marketing email
                </p>
              </div>

              <div className="border-l-4 border-purple-600 dark:border-purple-400 pl-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Data Portability</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Request a machine-readable copy of your data at any time
                </p>
              </div>

              <div className="border-l-4 border-purple-600 dark:border-purple-400 pl-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">GDPR & CCPA Rights</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  EU and California residents have additional rights. Contact privacy@boop.com for requests.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Data Security</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">We implement industry-standard security measures:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>HTTPS encryption for all connections</li>
              <li>Passwords hashed with bcrypt via Firebase Authentication</li>
              <li>Secure payment processing (PCI-DSS compliant via Stripe/PayPal)</li>
              <li>Regular security audits and vulnerability scanning</li>
              <li>Access controls and role-based permissions</li>
              <li>Automated backups and disaster recovery</li>
              <li>Monitoring for suspicious activity and fraud</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mt-4">
              However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute
              security but work continuously to protect your data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li><strong>Account Data:</strong> Retained while your account is active</li>
              <li><strong>Content:</strong> Retained indefinitely unless you delete it (may be anonymized on account deletion)</li>
              <li><strong>Transaction Records:</strong> Retained for 7 years for tax and legal compliance</li>
              <li><strong>Analytics Data:</strong> Aggregated and anonymized after 26 months</li>
              <li><strong>Logs:</strong> Server logs retained for 90 days for security purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Children's Privacy</h2>
            <p className="text-gray-700 dark:text-gray-300">
              Boop is not intended for users under 13 years of age. We do not knowingly collect personal information
              from children under 13. If we discover that a child under 13 has provided us with personal information,
              we will delete it immediately. Parents or guardians who believe their child has provided information
              should contact us at privacy@boop.com.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. International Data Transfers</h2>
            <p className="text-gray-700 dark:text-gray-300">
              Your information may be transferred to and maintained on servers located outside your state, province,
              country, or other governmental jurisdiction. By using Boop, you consent to such transfers. We ensure
              appropriate safeguards are in place for international transfers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 dark:text-gray-300">
              We may update this Privacy Policy from time to time. We will notify users of material changes via email
              and prominent on-site notification. The "Last Updated" date at the top will reflect when changes were made.
              Continued use of the Service after changes constitutes acceptance of the new Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Contact Us</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              For questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:
            </p>
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6">
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Email:</strong>{' '}
                <a href="mailto:privacy@boop.com" className="text-purple-600 dark:text-purple-400 hover:underline">
                  privacy@boop.com
                </a>
              </p>
              <p className="text-gray-700 dark:text-gray-300 mt-2">
                <strong>Data Protection Officer:</strong> dpo@boop.com
              </p>
              <p className="text-gray-700 dark:text-gray-300 mt-2">
                <strong>Response Time:</strong> We aim to respond within 48 hours
              </p>
            </div>
          </section>

          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6 mt-8">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Your Privacy Matters</h3>
            <p className="text-gray-700 dark:text-gray-300">
              We're committed to transparency about how we collect, use, and protect your data. If you have questions
              or concerns, please don't hesitate to reach out. We're here to help.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
